import { useMemo, useState } from 'react';
import { Icon } from '../chrome/Icon';
import { Sheet } from '../shared/Sheet';
import { useT } from '../../i18n';
import {
  useBabies,
  useBabyAccess,
  useBabyInvites,
  useCreateInvite,
  useEncryptionSettings,
  useMe,
  useRemoveAccess,
  useRevokeInvite,
  useUpdateAccessRole,
} from '../../data/queries';
import type { BabyAccessRole } from '../../data/dto';

interface Props {
  open: boolean;
  babyId: string;
  babyName: string;
  onClose: () => void;
}

type ShareRole = Exclude<BabyAccessRole, 'owner'>;

const SHARE_ROLES: ShareRole[] = ['editor', 'contributor', 'viewer'];

function roleLabel(t: ReturnType<typeof useT>, role: BabyAccessRole): string {
  switch (role) {
    case 'owner':       return t('sharing.role.owner', 'Owner');
    case 'editor':      return t('sharing.role.editor', 'Partner / Editor');
    case 'contributor': return t('sharing.role.contributor', 'Can add photos & comments');
    case 'viewer':      return t('sharing.role.viewer', 'Read only');
  }
}

function roleHint(t: ReturnType<typeof useT>, role: ShareRole): string {
  switch (role) {
    case 'editor':      return t('sharing.hint.editor', 'Full access — edit everything and invite others.');
    case 'contributor': return t('sharing.hint.contributor', 'Can view, plus add photos, comments and reactions.');
    case 'viewer':      return t('sharing.hint.viewer', 'Can view everything. Cannot add or change anything.');
  }
}

function formatExpiry(t: ReturnType<typeof useT>, iso: string | null): string {
  if (!iso) return t('sharing.noExpiry', 'No expiry');
  const d = new Date(iso.replace(' ', 'T') + 'Z');
  if (Number.isNaN(d.getTime())) return '';
  const days = Math.ceil((d.getTime() - Date.now()) / 86_400_000);
  if (days <= 0) return t('sharing.expired', 'Expired');
  if (days === 1) return t('sharing.expiresDay', 'Expires in 1 day');
  return t('sharing.expiresDays', 'Expires in {n} days').replace('{n}', String(days));
}

/**
 * Owner/editor surface for sharing a baby: issue invite links scoped to a
 * permission level, see who has joined, change their level, and revoke links
 * or remove members. The owner row is always locked.
 */
export function SharingSheet({ open, babyId, babyName, onClose }: Props) {
  const t = useT();
  const me = useMe(open);
  const babies = useBabies(open);
  const access = useBabyAccess(open ? babyId : null);
  const invites = useBabyInvites(open ? babyId : null);
  const encryption = useEncryptionSettings(open ? babyId : null, open);
  const e2ee = encryption.data?.mode === 'e2ee';
  const canNativeShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';

  const createInvite = useCreateInvite();
  const revokeInvite = useRevokeInvite();
  const updateRole = useUpdateAccessRole();
  const removeAccess = useRemoveAccess();

  const myUserId = me.data?.user.id ?? null;
  const myRole = useMemo(
    () => babies.data?.find((b) => b.id === babyId)?.role ?? null,
    [babies.data, babyId],
  );
  const canManage = myRole === 'owner' || myRole === 'editor';

  const [newRole, setNewRole] = useState<ShareRole>('editor');
  const [copied, setCopied] = useState<string | null>(null);

  const inviteUrl = (token: string) => `${window.location.origin}/?invite=${token}`;

  const copy = async (value: string, key: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(key);
      window.setTimeout(() => setCopied((c) => (c === key ? null : c)), 1600);
    } catch {
      /* clipboard blocked — no-op */
    }
  };

  // Native share sheet (iOS/Android/desktop). Falls back to copying the link
  // when the Web Share API isn't available or the user cancels.
  const share = async (token: string, key: string) => {
    const url = inviteUrl(token);
    const text = e2ee
      ? t('sharing.shareTextEnc', 'Join {name} on BabyNook. Photos are encrypted — I\'ll send you the password separately.').replace('{name}', babyName)
      : t('sharing.shareText', 'Join {name} on BabyNook.').replace('{name}', babyName);
    if (canNativeShare) {
      try {
        await navigator.share({ title: t('sharing.shareTitle', 'BabyNook invite'), text, url });
        return;
      } catch {
        return; // user dismissed the share sheet
      }
    }
    copy(url, key);
  };

  return (
    <Sheet open={open} onClose={onClose} size="lg" maxHeight="92dvh" label={t('sharing.title', 'Sharing & access')}>
      <div className="flex flex-col gap-7 overflow-y-auto px-6 pb-8 pt-0">
        {/* Close-row */}
        <div className="flex items-center justify-between">
          <span className="text-[11.5px] font-semibold uppercase tracking-[0.12em] text-ink-3">
            {t('sharing.title', 'Sharing & access')}
          </span>
          <button
            onClick={onClose}
            aria-label={t('common.close', 'Close')}
            className="-mr-2.5 flex min-h-11 min-w-11 items-center justify-center"
          >
            <Icon name="close" size={22} color="var(--color-ink)" />
          </button>
        </div>

        <div>
          <h1 className="font-serif text-[26px] leading-tight tracking-tight text-ink">
            {t('sharing.heading', 'Share {name}').replace('{name}', babyName)}
          </h1>
          <p className="mt-1.5 text-[13.5px] leading-relaxed text-ink-2 text-pretty">
            {t('sharing.desc', 'Invite a partner, grandparent or carer. They open the link or enter the code, then join with their own account.')}
          </p>
        </div>

        {/* Encryption reminder — photos are E2E encrypted, so the invitee also
            needs the password (shared out-of-band) to see them. */}
        {e2ee && (
          <div
            className="flex gap-2.5 rounded-2xl px-3.5 py-3"
            style={{ background: 'color-mix(in oklch, var(--color-accent) 9%, var(--color-surface))', border: '1px solid color-mix(in oklch, var(--color-accent) 28%, var(--color-line))' }}
          >
            <span className="mt-0.5 shrink-0 text-[15px]" aria-hidden>🔒</span>
            <p className="text-[12.5px] leading-snug text-ink-2">
              {t('sharing.encHint', 'Your photos are end-to-end encrypted. Share your encryption password with this person separately (not in the invite) so they can view photos.')}
            </p>
          </div>
        )}

        {/* ── Create an invite ──────────────────────────────────────── */}
        {canManage && (
          <section>
            <h2 className="mb-2.5 font-serif text-lg tracking-tight text-ink">
              {t('sharing.createTitle', 'Create an invite')}
            </h2>
            <div className="rounded-2xl border border-line bg-surface p-3.5">
              <span className="block text-[11.5px] font-semibold uppercase tracking-[0.12em] text-ink-3">
                {t('sharing.permission', 'Permission level')}
              </span>
              <div className="mt-2.5 flex flex-col gap-2">
                {SHARE_ROLES.map((r) => {
                  const on = newRole === r;
                  return (
                    <button
                      key={r}
                      type="button"
                      onClick={() => setNewRole(r)}
                      className={[
                        'flex items-start gap-3 rounded-xl border px-3.5 py-2.5 text-left transition-colors',
                        on ? 'border-ink bg-bg' : 'border-line bg-surface',
                      ].join(' ')}
                    >
                      <span
                        className={[
                          'mt-0.5 flex size-4.5 shrink-0 items-center justify-center rounded-full border',
                          on ? 'border-ink bg-ink' : 'border-line',
                        ].join(' ')}
                      >
                        {on && <Icon name="check" size={12} color="var(--color-bg)" />}
                      </span>
                      <span className="min-w-0">
                        <span className="block text-[14px] font-semibold text-ink">{roleLabel(t, r)}</span>
                        <span className="mt-0.5 block text-[12px] leading-snug text-ink-2">{roleHint(t, r)}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
              <button
                onClick={() => createInvite.mutate({ babyId, role: newRole })}
                disabled={createInvite.isPending}
                className="mt-3.5 inline-flex w-full min-h-12 items-center justify-center gap-2 rounded-3xl bg-ink px-5 text-[14px] font-semibold text-bg disabled:opacity-50"
              >
                {createInvite.isPending ? t('common.saving', 'Saving…') : t('sharing.createBtn', 'Create invite link')}
                {!createInvite.isPending && <Icon name="plus" size={16} color="currentColor" />}
              </button>
            </div>
          </section>
        )}

        {/* ── Active invite links ───────────────────────────────────── */}
        {canManage && (
          <section>
            <h2 className="mb-2.5 font-serif text-lg tracking-tight text-ink">
              {t('sharing.linksTitle', 'Active invites')}
            </h2>
            {(invites.data?.length ?? 0) === 0 ? (
              <p className="rounded-2xl border border-dashed border-line bg-surface px-4 py-5 text-center text-[13px] text-ink-2">
                {t('sharing.noLinks', 'No active invite links. Create one above to share access.')}
              </p>
            ) : (
              <div className="flex flex-col gap-3">
                {invites.data!.map((inv) => (
                  <div key={inv.id} className="overflow-hidden rounded-2xl border border-line bg-surface">
                    {/* Header: role + expiry */}
                    <div className="flex items-center justify-between gap-2 px-4 pt-3.5">
                      <span
                        className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold text-ink"
                        style={{ background: 'color-mix(in oklch, var(--color-accent) 14%, var(--color-bg))' }}
                      >
                        {roleLabel(t, inv.role)}
                      </span>
                      <span className="text-[11.5px] text-ink-3">{formatExpiry(t, inv.expires_at)}</span>
                    </div>

                    {/* The code, front and centre */}
                    <div className="px-4 pt-2.5">
                      <div className="flex items-center justify-center rounded-xl border border-dashed border-line bg-bg px-3 py-3">
                        <code className="font-mono text-[18px] font-semibold tracking-[0.18em] text-ink">{inv.token}</code>
                      </div>
                    </div>

                    {/* Actions: prominent Share + copy code */}
                    <div className="flex gap-2 px-4 pt-3">
                      <button
                        onClick={() => share(inv.token, `link-${inv.id}`)}
                        className="inline-flex min-h-11 flex-1 items-center justify-center gap-2 rounded-2xl bg-ink px-4 text-[13.5px] font-semibold text-bg"
                      >
                        <ShareGlyph />
                        {canNativeShare
                          ? t('sharing.share', 'Share')
                          : copied === `link-${inv.id}`
                          ? t('sharing.copied', 'Link copied')
                          : t('sharing.copyLink', 'Copy link')}
                      </button>
                      <button
                        onClick={() => copy(inv.token, `code-${inv.id}`)}
                        className="inline-flex min-h-11 items-center justify-center rounded-2xl border border-line px-3.5 text-[13px] font-semibold text-ink-2"
                      >
                        {copied === `code-${inv.id}` ? t('sharing.copied', 'Copied') : t('sharing.copyCode', 'Copy code')}
                      </button>
                    </div>

                    {/* Footer: joined count + revoke */}
                    <div className="mt-3 flex items-center justify-between border-t border-hairline px-4 py-2.5">
                      <span className="text-[11.5px] text-ink-3">
                        {t('sharing.joinedCount', '{n} joined').replace('{n}', String(inv.use_count))}
                      </span>
                      <button
                        onClick={() => revokeInvite.mutate({ babyId, inviteId: inv.id })}
                        disabled={revokeInvite.isPending}
                        className="inline-flex items-center gap-1 text-[12px] font-semibold text-[#7E3320] disabled:opacity-50"
                      >
                        <Icon name="trash" size={14} color="#7E3320" />
                        {t('sharing.revoke', 'Revoke')}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        {/* ── People with access ────────────────────────────────────── */}
        <section>
          <h2 className="mb-2.5 font-serif text-lg tracking-tight text-ink">
            {t('sharing.membersTitle', 'People with access')}
          </h2>
          <div className="overflow-hidden rounded-2xl border border-line bg-surface">
            {(access.data ?? []).map((m, i, arr) => {
              const isOwner = m.role === 'owner';
              const isMe = m.user_id === myUserId;
              const name = m.display_name?.trim() || m.user_email;
              return (
                <div
                  key={m.id}
                  className={[
                    'flex flex-col gap-2 px-3.5 py-3',
                    i < arr.length - 1 ? 'border-b border-hairline' : '',
                  ].join(' ')}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="min-w-0">
                      <span className="block truncate text-[14.5px] font-semibold text-ink">
                        {name}
                        {isMe && <span className="ml-1.5 text-[11.5px] font-medium text-ink-3">{t('sharing.you', '(you)')}</span>}
                      </span>
                      {m.display_name && (
                        <span className="block truncate text-[12px] text-ink-2">{m.user_email}</span>
                      )}
                    </span>
                    {(isOwner || !canManage) && (
                      <span className="shrink-0 rounded-full bg-grey-soft px-2.5 py-0.5 text-[11px] font-semibold text-ink-2">
                        {roleLabel(t, m.role)}
                      </span>
                    )}
                  </div>

                  {canManage && !isOwner && (
                    <div className="flex items-center gap-2">
                      <select
                        value={m.role}
                        onChange={(e) =>
                          updateRole.mutate({ babyId, userId: m.user_id, role: e.target.value as ShareRole })
                        }
                        disabled={updateRole.isPending}
                        className="min-h-9 flex-1 rounded-lg border border-line bg-bg px-2.5 text-[13px] text-ink outline-none disabled:opacity-50"
                      >
                        {SHARE_ROLES.map((r) => (
                          <option key={r} value={r}>{roleLabel(t, r)}</option>
                        ))}
                      </select>
                      <button
                        onClick={() => removeAccess.mutate({ babyId, userId: m.user_id })}
                        disabled={removeAccess.isPending}
                        aria-label={t('sharing.removeMember', 'Remove access')}
                        className="inline-flex min-h-9 items-center gap-1 rounded-lg border border-line px-2.5 text-[12px] font-semibold text-[#7E3320] disabled:opacity-50"
                      >
                        <Icon name="trash" size={14} color="#7E3320" />
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      </div>
    </Sheet>
  );
}

/** Small share-arrow glyph for the Share button (Icon has no 'share'). */
function ShareGlyph() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M12 3v12" />
      <path d="M8 7l4-4 4 4" />
      <path d="M5 12v6a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-6" />
    </svg>
  );
}
